import { Plus, X } from "lucide-react";
import { Button } from "./button.tsx";
import { FieldInput, type FieldInputLabels, type FieldInputSpec, type FieldValue } from "./field-input.tsx";
import type { NumberSeparators } from "./money.ts";

export type FilterOp = "eq" | "contains" | "starts_with" | "gte" | "lte" | "is_null";
export interface FilterCondition {
  readonly fieldId: string;
  readonly op: FilterOp;
  readonly value: FieldValue;
}

export const OPS_BY_TYPE: Record<FieldInputSpec["type"], readonly FilterOp[]> = {
  text: ["contains", "starts_with", "eq", "is_null"],
  number: ["eq", "gte", "lte", "is_null"],
  money: ["eq", "gte", "lte", "is_null"],
  date: ["eq", "gte", "lte", "is_null"],
  bool: ["eq", "is_null"],
  select: ["eq", "is_null"],
  relation: ["eq", "is_null"],
};

/** Điều kiện ⇒ SecretAAExpression (ADR-003 mục 7): AND các điều kiện đủ giá trị; không điều kiện nào ⇒ null. */
export function toExpression(conditions: readonly FilterCondition[], fields: readonly FieldInputSpec[]): unknown {
  const parts = conditions.flatMap((c): unknown[] => {
    const f = fields.find((x) => x.id === c.fieldId);
    if (!f) return [];
    if (c.op === "is_null") return [{ op: "is_null", arg: { field: f.id } }];
    if (c.value === null || c.value === "") return [];
    const literal =
      f.type === "number" ? { number: String(c.value) } : f.type === "money" ? { money: String(c.value), currency: f.currency ?? "" } : f.type === "date" ? { date: String(c.value) } : c.value;
    return [{ op: c.op, left: { field: f.id }, right: { literal } }];
  });
  if (parts.length === 0) return null;
  return parts.length === 1 ? parts[0] : { op: "and", args: parts };
}

export interface FilterBuilderLabels extends FieldInputLabels {
  readonly title: string;
  readonly field: string;
  readonly operator: string;
  readonly add: string;
  readonly remove: string;
  readonly op: (op: FilterOp) => string;
}

/** Thanh lọc sinh SecretAAExpression; chỉ field bật lọc (indexed) mới được đưa vào `fields`. */
export function FilterBuilder({ fields, conditions, onChange, labels, separators }: { fields: readonly FieldInputSpec[]; conditions: readonly FilterCondition[]; onChange: (next: FilterCondition[]) => void; labels: FilterBuilderLabels; separators: NumberSeparators }) {
  const set = (i: number, patch: Partial<FilterCondition>) => onChange(conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const selectCls = "h-9 rounded-md border border-(--saa-border) bg-(--saa-surface) px-2 text-sm";
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="sr-only">{labels.title}</legend>
      {conditions.map((c, i) => {
        const f = fields.find((x) => x.id === c.fieldId) ?? fields[0];
        if (!f) return null;
        return (
          <div key={i} className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-xs font-medium">
              {labels.field}
              <select className={selectCls} value={f.id} onChange={(e) => set(i, { fieldId: e.target.value, op: OPS_BY_TYPE[fields.find((x) => x.id === e.target.value)!.type][0]!, value: null })}>
                {fields.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium">
              {labels.operator}
              <select className={selectCls} value={c.op} onChange={(e) => set(i, { op: e.target.value as FilterOp })}>
                {OPS_BY_TYPE[f.type].map((op) => (
                  <option key={op} value={op}>
                    {labels.op(op)}
                  </option>
                ))}
              </select>
            </label>
            {c.op !== "is_null" ? (
              <div className="min-w-48">
                <FieldInput key={`${f.id}-${i}`} field={{ ...f, required: false }} value={c.value} onChange={(v) => set(i, { value: v })} labels={labels} separators={separators} />
              </div>
            ) : null}
            <Button variant="ghost" aria-label={labels.remove} onClick={() => onChange(conditions.filter((_, j) => j !== i))}>
              <X size={14} aria-hidden />
            </Button>
          </div>
        );
      })}
      {fields.length ? (
        <div>
          <Button variant="secondary" onClick={() => onChange([...conditions, { fieldId: fields[0]!.id, op: OPS_BY_TYPE[fields[0]!.type][0]!, value: null }])}>
            <Plus size={14} aria-hidden />
            {labels.add}
          </Button>
        </div>
      ) : null}
    </fieldset>
  );
}
